import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import { ArrowRight, BarChart3, Layers3, Rocket, Search, ShieldCheck, SlidersHorizontal, Sparkles } from "lucide-react";
import "../styles/projects.css";
import SeoHead from "../components/SeoHead";
import PROJECT_CATEGORIES from "../constants/projectCategories";

const fallbackProjects = [
  {
    _id: "fallback-taxi",
    title: "Full-stack taxi operations platform",
    category: "Transportation & Mobility",
    publishedBy: "In-house",
    date: "2025-01-01",
    image: "",
    sections: {
      Introduction:
        "MERN system for trip reconciliation, live driver monitoring, and automated dispatch; reduced missed trips and improved SLA adherence.",
    },
  },
  {
    _id: "fallback-ats",
    title: "Ethical AI resume screening engine",
    category: "Recruiting & Workforce",
    publishedBy: "Old Alex Hub",
    date: "2025-01-01",
    image: "",
    sections: {
      Introduction:
        "Transformer-based semantic matching to surface best-fit candidates, reducing bias and increasing hiring speed.",
    },
  },
  {
    _id: "fallback-forecast",
    title: "YouTube trending prediction",
    category: "Predictive Analytics",
    publishedBy: "Old Alex Hub",
    date: "2025-01-01",
    image: "",
    sections: {
      Introduction:
        "ML model using metadata and NLP to forecast video virality for content planning and publisher strategy.",
    },
  },
];

const ntdIntelligenceProject = {
  _id: "ntd-intelligence",
  title: "NTD Intelligence",
  category: "Transportation & Mobility",
  publishedBy: "Mohamed Gad",
  date: "2026-08-22T12:00:00.000Z",
  image: "",
  isNtdIntelligence: true,
  tags: ["Transit Intelligence", "Federal Data", "Analytics", "Decision Support", "Interactive"],
  sections: {
    Introduction:
      "Turning the National Transit Database into intuitive, decision-ready intelligence for transit professionals. Explore agencies, benchmark peers, and analyze ridership, service, costs, funding, fleet, and safety without having to decode the NTD.",
  },
};

const AI_KEYWORDS = ["ai", "ml", "machine learning", "forecast", "nlp", "automation", "predict", "chatbot"];
const OPS_KEYWORDS = ["ops", "operations", "platform", "dispatch", "lifecycle", "management", "reconciliation"];
const CATEGORY_FALLBACKS = [
  {
    category: "Transportation & Mobility",
    keywords: ["transit", "transport", "mobility", "taxi", "dispatch", "shuttle", "paratransit", "ride"],
  },
  {
    category: "AI Assistants & Language Tools",
    keywords: ["chatbot", "copilot", "assistant", "semantic", "language", "search", "dictionary", "tts", "movie"],
  },
  {
    category: "Enterprise AI & Automation",
    keywords: ["workflow", "automation", "internal", "business operations", "staffing", "schedule"],
  },
  {
    category: "Recruiting & Workforce",
    keywords: ["resume", "recruit", "hiring", "job", "candidate", "workforce"],
  },
  {
    category: "Healthcare AI",
    keywords: ["disease", "diabetes", "clinical", "healthcare", "symptom", "patient"],
  },
  {
    category: "Real Estate Analytics",
    keywords: ["real estate", "property", "aqari", "housing"],
  },
  {
    category: "Recommendation Systems",
    keywords: ["recommend", "playlist", "music", "meal", "vacation", "travel"],
  },
  {
    category: "Energy & EV Analytics",
    keywords: ["ev", "battery", "range", "voltai", "electric vehicle"],
  },
  {
    category: "Computer Vision & Robotics",
    keywords: ["self driving", "behavioral cloning", "steering", "vision", "simulator"],
  },
  {
    category: "Full-Stack Platforms & Tools",
    keywords: ["full-stack", "desktop", "tool", "store", "calculator", "platform"],
  },
];

const getProjectKey = (project) => project._id || project.title;

const formatProjectDate = (date) => {
  if (!date) return "2025";
  return new Date(date).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const resolveCategory = (project) => {
  if (project.category) return project.category;

  const searchableText = [
    project.title,
    project.subtitle,
    project.summary,
    project.description,
    project.sections?.Introduction,
    project.sections?.Abstract,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const matched = CATEGORY_FALLBACKS.find(({ keywords }) => keywords.some((keyword) => searchableText.includes(keyword)));
  return matched?.category || "Predictive Analytics";
};

const sortProjects = (list, sortBy) => {
  const sorted = [...list];

  if (sortBy === "oldest") {
    return sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  if (sortBy === "az") {
    return sorted.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  }

  return sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
};

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, ai: 0, ops: 0 });
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [visibleCount, setVisibleCount] = useState(6);
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const computeStats = (list) => {
    const norm = (txt = "") => txt.toLowerCase();
    const ai = list.filter(
      (p) =>
        AI_KEYWORDS.some((k) => norm(p.title || "").includes(k)) ||
        AI_KEYWORDS.some((k) => norm(p.sections?.Introduction || "").includes(k))
    ).length;
    const ops = list.filter(
      (p) =>
        OPS_KEYWORDS.some((k) => norm(p.title || "").includes(k)) ||
        OPS_KEYWORDS.some((k) => norm(p.sections?.Introduction || "").includes(k))
    ).length;
    return { total: list.length, ai, ops };
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await axios.get(API_ENDPOINTS.getArticles);
        if (response.status === 200 && response.data?.articles) {
          setProjects(response.data.articles);
          setStats(computeStats(response.data.articles));
        } else {
          setProjects(fallbackProjects);
          setStats(computeStats(fallbackProjects));
        }
      } catch (err) {
        setError("Projects API unavailable. Showing highlights instead.");
        setProjects(fallbackProjects);
        setStats(computeStats(fallbackProjects));
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const heroStats = useMemo(
    () => [
      { label: "Projects shipped", value: stats.total || fallbackProjects.length },
      { label: "AI/ML builds", value: stats.ai || "-" },
      { label: "Ops platforms", value: stats.ops || "-" },
      { label: "Audit-ready reports", value: "Monthly" },
    ],
    [stats]
  );

  const truncateText = (text = "", maxWords = 36) => {
    const cleaned = text.replace(/^['"*]+|['"*]+$/g, "").replace(/\*/g, "");
    const words = cleaned.split(/\s+/);
    return words.length > maxWords ? `${words.slice(0, maxWords).join(" ")}...` : cleaned;
  };

  const cards = projects.length > 0 ? projects : fallbackProjects;
  const normalizedCards = useMemo(
    () => [ntdIntelligenceProject, ...cards.filter((project) => project._id !== "ntd-intelligence")]
      .map((project) => ({ ...project, category: resolveCategory(project) })),
    [cards]
  );

  const categoryCounts = useMemo(() => {
    return normalizedCards.reduce(
      (acc, project) => {
        acc[project.category] = (acc[project.category] || 0) + 1;
        return acc;
      },
      { All: normalizedCards.length }
    );
  }, [normalizedCards]);

  const categoryOptions = useMemo(
    () => ["All", ...PROJECT_CATEGORIES.filter((category) => categoryCounts[category] > 0)],
    [categoryCounts]
  );

  const featuredProjects = useMemo(() => {
    const sorted = sortProjects(normalizedCards.filter((project) => !project.isNtdIntelligence), "newest");
    if (sorted.length === 0) return [];

    const anchorProject = sorted[0];
    const picked = [anchorProject, ntdIntelligenceProject];
    const seenCategories = new Set([anchorProject.category, ntdIntelligenceProject.category]);
    const remaining = sorted.slice(1);

    for (const project of remaining) {
      if (seenCategories.has(project.category)) continue;

      picked.push(project);
      seenCategories.add(project.category);

      if (picked.length === 3) return picked;
    }

    return picked;
  }, [normalizedCards]);

  const featuredKeys = useMemo(
    () => new Set(featuredProjects.map((project) => getProjectKey(project))),
    [featuredProjects]
  );

  const filteredProjects = useMemo(() => {
    const normalizedQuery = deferredSearchTerm.trim().toLowerCase();

    const filtered = normalizedCards.filter((project) => {
      const inCategory = activeCategory === "All" || project.category === activeCategory;
      if (!inCategory) return false;

      if (!normalizedQuery) return true;

      const searchableText = [
        project.title,
        project.subtitle,
        project.category,
        project.publishedBy,
        project.sections?.Introduction,
        project.sections?.Abstract,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedQuery);
    });

    return sortProjects(filtered, sortBy);
  }, [activeCategory, deferredSearchTerm, normalizedCards, sortBy]);

  const hideFeaturedFromArchive = activeCategory === "All" && !deferredSearchTerm.trim() && sortBy === "newest";
  const archiveProjects = useMemo(() => {
    if (!hideFeaturedFromArchive) return filteredProjects;
    return filteredProjects.filter((project) => !featuredKeys.has(getProjectKey(project)));
  }, [featuredKeys, filteredProjects, hideFeaturedFromArchive]);

  const visibleProjects = useMemo(
    () => archiveProjects.slice(0, visibleCount),
    [archiveProjects, visibleCount]
  );

  useEffect(() => {
    setVisibleCount(6);
  }, [activeCategory, deferredSearchTerm, sortBy]);

  const clearFilters = () => {
    setActiveCategory("All");
    setSearchTerm("");
    setSortBy("newest");
  };

  const renderProjectCard = (project, featured = false, featuredLabel = "") => {
    const intro =
      project.sections?.Introduction ||
      project.summary ||
      project.description ||
      "Operational and AI-driven case study.";
    const cover = project.image;
    const hasDetails = project._id && !String(project._id).startsWith("fallback-");

    return (
      <div key={getProjectKey(project)} className={featured ? "col-lg-4 col-md-6" : "col-lg-4 col-md-6"}>
        <div className={`project-card h-100 ${featured ? "featured" : ""} ${project.isNtdIntelligence ? "ntd-project-card" : ""}`}>
          {cover ? (
            <div
              className="project-thumb"
              style={{ backgroundImage: `url(${cover})` }}
              role="img"
              aria-label={project.title}
            />
          ) : project.isNtdIntelligence ? (
            <div className="project-thumb ntd-project-thumb" role="img" aria-label="NTD Intelligence transit data visual">
              <span className="ntd-project-mark">NTD <i /> INTELLIGENCE</span>
              <div className="ntd-project-lines"><i /><i /><i /><i /><i /><i /><i /></div>
              <div className="ntd-project-signal"><b>Transit data</b><span>to decision intelligence</span></div>
            </div>
          ) : (
            <div className="project-thumb placeholder">
              <Rocket size={20} />
            </div>
          )}
          <div className="project-body">
            <div className="meta-row">
              <span className="meta">{project.publishedBy || "Internal"}</span>
              <span className="meta">{formatProjectDate(project.date)}</span>
            </div>
            {featuredLabel ? <span className="featured-label">{featuredLabel}</span> : null}
            <span className="project-category">{project.category}</span>
            <h5 className="mb-1">{project.title}</h5>
            <p className="text-muted small mb-3">{truncateText(intro, featured ? 28 : 32)}</p>
            {hasDetails ? (
              <Link to={`/projects/${project._id || ""}`} className="btn btn-outline-primary btn-sm mt-auto">
                {project.isNtdIntelligence ? "Explore NTD Intelligence" : "Learn more"} <ArrowRight size={14} className="ms-1" />
              </Link>
            ) : (
              <button type="button" className="btn btn-outline-secondary btn-sm mt-auto" disabled>
                Preview unavailable
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="projects-page">
      <SeoHead
        title="Projects and Case Studies in AI, Data Science, and Transportation Analytics"
        description="Selected projects by Mohamed Gad across AI, data science, and transportation analytics, showcasing operational platforms, ethical AI, and mobility systems."
        keywords={[
          "Mohamed Gad projects",
          "AI case studies",
          "data science portfolio",
          "transportation analytics projects",
          "mobility systems",
          "ethical AI",
          "operational analytics",
        ]}
        canonical="https://www.mohamedgad.com/projects"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Projects and Case Studies - Mohamed Gad",
            "url": "https://www.mohamedgad.com/projects",
            "description": "AI, data science, and transportation analytics projects led by Mohamed Gad, focusing on mobility systems and ethical automation.",
            "about": [
              "transportation analytics",
              "mobility systems",
              "AI for operations",
              "data science",
              "ethical AI",
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.mohamedgad.com/" },
              { "@type": "ListItem", "position": 2, "name": "Projects", "item": "https://www.mohamedgad.com/projects" }
            ]
          }
        ]}
      />
      <section className="container py-5">
        <div className="projects-hero">
          <div>
            <p className="eyebrow">Selected work</p>
            <h1 className="display-6 fw-bold">Projects and case studies</h1>
            <p className="lead">
              AI for operational efficiency, reconciliation platforms, and ethical decision systems that stand up to
              audits.
            </p>
            <div className="projects-tags">
              <span className="tag">Operational analytics</span>
              <span className="tag">AI for efficiency</span>
              <span className="tag">Data governance</span>
              <span className="tag">Full-stack delivery</span>
            </div>
          </div>
          <div className="hero-metrics">
            {heroStats.map((s) => (
              <div key={s.label} className="metric-card">
                <div className="metric-value">{s.value}</div>
                <div className="metric-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container pb-4">
        <div className="projects-section-head">
          <div>
            <p className="eyebrow">Start here</p>
            <h2 className="fw-bold mb-1">Featured case studies</h2>
            <p className="text-muted mb-0">
              A quick cross-section of the portfolio so visitors can understand the range without diving into the full archive first.
            </p>
          </div>
          <div className="results-card">
            <div className="results-value">{normalizedCards.length}</div>
            <div className="results-label">projects in the archive</div>
          </div>
        </div>

        <div className="row g-4">
          {featuredProjects.map((project, index) =>
            renderProjectCard(project, true, index === 0 ? "Latest project" : "")
          )}
        </div>
      </section>

      <section className="container pb-5">
        {error ? (
          <div className="alert alert-warning" role="alert">
            {error}
          </div>
        ) : null}

        <div className="projects-controls">
          <div className="controls-top">
            <div>
              <p className="eyebrow mb-1">Browse archive</p>
              <h2 className="fw-bold mb-1">Find the right project faster</h2>
              <p className="text-muted mb-0">
                Filter by category, search by topic, and reveal more only when you want it.
              </p>
            </div>
            <div className="results-card compact">
              <div className="results-value">{filteredProjects.length}</div>
              <div className="results-label">matching projects</div>
            </div>
          </div>

          <div className="controls-row">
            <label className="search-shell">
              <Search size={17} />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by project, topic, or system"
                aria-label="Search projects"
              />
            </label>

            <label className="sort-shell">
              <SlidersHorizontal size={17} />
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort projects">
                <option value="newest">Most recent</option>
                <option value="oldest">Oldest first</option>
                <option value="az">A to Z</option>
              </select>
            </label>
          </div>

          <div className="filter-row" aria-label="Project categories">
            {categoryOptions.map((category) => (
              <button
                key={category}
                type="button"
                className={`filter-chip ${activeCategory === category ? "active" : ""}`}
                onClick={() => setActiveCategory(category)}
              >
                <span>{category}</span>
                <span className="filter-count">{categoryCounts[category] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="project-grid-summary">
          <div className="summary-copy">
            {archiveProjects.length > 0
              ? hideFeaturedFromArchive
                ? `Showing ${visibleProjects.length} of ${archiveProjects.length} additional projects`
                : `Showing ${visibleProjects.length} of ${archiveProjects.length} projects`
              : "No projects match the current view"}
          </div>
          {(activeCategory !== "All" || searchTerm || sortBy !== "newest") && (
            <button type="button" className="btn btn-link btn-sm p-0" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>

        <div className="row g-4">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="col-md-4">
                  <div className="project-card skeleton" />
                </div>
              ))
            : visibleProjects.length > 0
              ? visibleProjects.map((project) => renderProjectCard(project))
              : (
                <div className="col-12">
                  <div className="empty-state">
                    <div className="icon-pill mx-auto">
                      <Layers3 size={18} />
                    </div>
                    <h5 className="mb-1">No projects found</h5>
                    <p className="text-muted mb-3">
                      Try a broader keyword or switch back to all categories.
                    </p>
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={clearFilters}>
                      Reset view
                    </button>
                  </div>
                </div>
              )}
        </div>

        {!loading && visibleProjects.length < archiveProjects.length ? (
          <div className="load-more-wrap">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => setVisibleCount((current) => current + 6)}
            >
              Load more projects
            </button>
          </div>
        ) : null}
      </section>

      <section className="container pb-5">
        <div className="info-grid">
          <div className="info-card">
            <div className="icon-pill">
              <BarChart3 size={18} />
            </div>
            <h5 className="mb-1">Reconciliation and compliance</h5>
            <p className="text-muted mb-0">
              Systems that reconcile 56k+ trips monthly, surface anomalies, and keep ADA/FTA reviews painless.
            </p>
          </div>
          <div className="info-card">
            <div className="icon-pill">
              <Sparkles size={18} />
            </div>
            <h5 className="mb-1">AI for decision velocity</h5>
            <p className="text-muted mb-0">
              Forecasting, NLP, and automation that speed up ops while keeping human judgment in the loop.
            </p>
          </div>
          <div className="info-card">
            <div className="icon-pill">
              <ShieldCheck size={18} />
            </div>
            <h5 className="mb-1">Governance and safety</h5>
            <p className="text-muted mb-0">
              Ethical, auditable tooling that reduces bias and keeps data compliant end to end.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
